import React, { useState } from 'react'
import { apiService as api } from '../../services/api'
import { Link } from 'react-router-dom'
import { Container } from '../../components/ui/Container'
import { Button } from '../../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Heart, Mail, Lock, Eye, EyeOff, User, Phone } from 'lucide-react'
import { motion } from 'framer-motion'

const SignUp: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [agreeToTerms, setAgreeToTerms] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!agreeToTerms) {
      alert('Please agree to the terms and conditions')
      return
    }
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match')
      return
    }
    setIsLoading(true)
    
    try {
      const response = await api.register({
        email: formData.email,
        password: formData.password,
        name: `${formData.firstName} ${formData.lastName}`
      })
      
      if (response.success && response.data) {
        // Store token if returned, or redirect to login
        if (response.data.token) {
           localStorage.setItem('token', response.data.token);
           window.location.href = '/dashboard';
        } else {
           // Maybe email verification required? For now assume login on signup
           window.location.href = '/auth/signin';
        }
      } else {
        alert(response.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error)
      alert('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <div className="min-h-screen bg-theme-bg text-theme-primary flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Container size="md" className="w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full"
        >
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center space-x-2 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-theme-primary to-theme-secondary rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-theme-primary">BioTune</span>
            </Link>
            <h1 className="text-3xl font-bold text-theme-primary mb-2">Create your account</h1>
            <p className="text-theme-secondary">Join thousands optimizing their health with personalized biomarker insights</p>
          </div>

          {/* Sign Up Card */}
          <Card className="border-theme-border shadow-xl hover:shadow-2xl transition-shadow duration-300 bg-theme-surface">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-bold text-theme-primary">Sign Up</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="firstName" className="block text-sm font-medium text-theme-secondary">
                      First Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-5 w-5 text-theme-muted" />
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-3 py-3 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-200 bg-theme-surface text-theme-primary"
                        placeholder="John"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="lastName" className="block text-sm font-medium text-theme-secondary">
                      Last Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-5 w-5 text-theme-muted" />
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-3 py-3 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-200 bg-theme-surface text-theme-primary"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-theme-secondary">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-theme-muted" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-200 bg-theme-surface text-theme-primary"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Phone Field */}
                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-sm font-medium text-theme-secondary">
                    Phone Number (Optional)
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-5 w-5 text-theme-muted" />
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-200 bg-theme-surface text-theme-primary"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-theme-secondary">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-theme-muted" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-12 py-3 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-200 bg-theme-surface text-theme-primary"
                      placeholder="Create a strong password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-theme-muted hover:text-theme-secondary transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-theme-secondary">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-theme-muted" />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-12 py-3 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-200 bg-theme-surface text-theme-primary"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-3 text-theme-muted hover:text-theme-secondary transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="flex items-start space-x-3">
                  <input
                    id="terms"
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="mt-1 rounded border-theme-border text-theme-primary focus:ring-theme-primary"
                  />
                  <label htmlFor="terms" className="text-sm text-theme-secondary leading-relaxed">
                    I agree to the{' '}
                    <Link to="/terms" className="text-theme-primary hover:text-theme-secondary font-medium">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-theme-primary hover:text-theme-secondary font-medium">
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading || !agreeToTerms}
                  className="w-full bg-gradient-to-r from-theme-primary to-theme-secondary hover:from-theme-primary-dark hover:to-theme-secondary-dark text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Creating account...</span>
                    </span>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Sign In Link */}
          <div className="text-center mt-6">
            <p className="text-theme-secondary">
              Already have an account?{' '}
              <Link
                to="/auth/signin"
                className="text-theme-primary hover:text-theme-secondary font-medium transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>

          {/* Why Join */}
          <div className="mt-8 p-4 bg-theme-elevated rounded-lg border border-theme-border">
            <h3 className="text-sm font-medium text-theme-primary mb-2">Why join BioTune?</h3>
            <ul className="text-xs text-theme-secondary space-y-1">
              <li>✓ Personalized health insights from 100+ biomarkers</li>
              <li>✓ AI-powered recommendations tailored to your profile</li>
              <li>✓ Access to health coaches and medical professionals</li>
              <li>✓ Track your progress with detailed analytics</li>
            </ul>
          </div>
        </motion.div>
      </Container>
    </div>
  )
}

export default SignUp
